<template>
  <div class="form-input-element-wrapper formatted">
    <FormLabel :element="element" :labelStyle="labelStyle" />
    <FormDescription :element="element" :labelStyle="labelStyle" />

    <div class="input-field-wrapper position-relative">
      <input
        type="text"
        class="form-input fw-normal fs-6 w-100 bg-transparent"
        :class="{ 'user-select-none': isDisabled }"
        :id="`input-${element.id}`"
        :style="fieldStyle"
        :name="element.name"
        :required="isRequired"
        :disabled="isDisabled"
        :placeholder="getPlaceholder"
        :value="displayValue"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus"
        @beforeinput="onBeforeInput"
        @paste="onPaste"
        inputmode="decimal"
        autocomplete="off"
      />

      <img
        v-if="element?.settings?.prefixIcon?.icon"
        :id="`prefix-icon-${element.id}`"
        class="prefix-icon position-absolute top-50"
        :src="element.settings.prefixIcon.icon"
      />

      <img
        class="error-icon position-absolute top-50"
        :src="require('@/assets/img/error-circle-exclamation.svg')"
      />
    </div>
  </div>
</template>

<script>
import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';
import { DEFAULT_FORM_STYLES } from '@/utils/constants/WorkFlowConstants';

export default {
  name: 'FormFormattedInputElement',
  props: {
    element: { type: Object, required: true },
    labelStyle: { type: Object, default: () => ({}) },
    fieldStyle: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    value: { type: [Number, String], default: '' },
    MAX_INTEGER_DIGITS: 15,
  },
  data() {
    return {
      internalValue: this.value ? String(this.value) : '',
      isFocused: false,
    };
  },
  methods: {
    parseGermanNumber(str) {
      if (!str) return null;
      str = String(str).trim().replace(/\s/g, '');

      if (/^-?\d+(\.\d+)?$/.test(str)) {
        const num = parseFloat(str);
        return isNaN(num) ? null : num;
      }

      const thSep = this.thousandSeparator;
      const decSep = this.decimalSeparator;

      const thSepEsc = thSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const decSepEsc = decSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      const parts = str.split(new RegExp(decSepEsc));
      let integerPart = parts[0].replace(new RegExp(thSepEsc, 'g'), '');
      let decimalPart = parts[1] || '';

      const normalized = integerPart + (decimalPart ? '.' + decimalPart : '');
      const num = parseFloat(normalized);
      return isNaN(num) ? null : num;
    },

    formatGermanNumber(num) {
      if (num == null || isNaN(num)) return '';

      const decSep = this.decimalSeparator;
      const thSep = this.thousandSeparator;

      const parts = num.toFixed(this.getDecimalCount).split('.'); // JS always uses '.' for decimals
      let integerPart = parts[0];
      const decimalPart = parts[1];

      integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thSep);

      return decimalPart ? integerPart + decSep + decimalPart : integerPart;
    },

    onBeforeInput(e) {
      if (
        e.inputType.startsWith('delete') ||
        e.inputType === 'insertFromPaste' ||
        e.inputType === 'insertFromDrop'
      )
        return;

      const decSep = this.decimalSeparator;
      const thSep = this.thousandSeparator;

      if (e.data === '.' || e.data === ',' || e.data === decSep) {
        e.preventDefault();
        const el = e.target;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        let separatorToInsert = decSep;

        if (e.data === thSep) {
          separatorToInsert = thSep;
        } else if (e.data !== decSep && e.data !== thSep) {
          separatorToInsert = decSep;
        }

        this.internalValue =
          this.internalValue.slice(0, start) +
          separatorToInsert +
          this.internalValue.slice(end);

        this.$nextTick(() => el.setSelectionRange(start + 1, start + 1));
        return;
      }

      const allowedChars = new RegExp(`[\\d${thSep}${decSep}]`);
      if (e.data && !allowedChars.test(e.data)) e.preventDefault();
      if (e.data === decSep && this.internalValue.includes(decSep))
        e.preventDefault();
    },

    onPaste(e) {
      e.preventDefault();
      const thSep = this.thousandSeparator;
      const decSep = this.decimalSeparator;

      const text = (e.clipboardData || window.clipboardData).getData('text');
      let clean = text
        .replace(/\s/g, '')
        .replace(new RegExp(`\\${thSep}`, 'g'), '')
        .replace(/[^0-9,\.]/g, '')
        .replace('.', decSep)
        .replace(',', decSep);

      const el = e.target;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const value = this.internalValue;

      this.internalValue = value.slice(0, start) + clean + value.slice(end);
      this.$nextTick(() => {
        const pos = start + clean.length;
        el.setSelectionRange(pos, pos);
      });
    },

    onInput(e) {
      this.internalValue = e.target.value;
    },

    onFocus(e) {
      this.isFocused = true;

      const clean = this.internalValue.replace(
        new RegExp(`\\${this.thousandSeparator}`, 'g'),
        ''
      );
      this.internalValue = clean;

      this.$nextTick(() => {
        e.target.setSelectionRange(clean.length, clean.length);
      });
    },

    onBlur() {
      this.isFocused = false;

      let clean = this.internalValue
        .replace(new RegExp(`\\${this.thousandSeparator}`, 'g'), '')
        .replace(this.decimalSeparator, '.');

      const [intPart, decPart = ''] = clean.split('.');
      const clampedInt = intPart.slice(0, this.MAX_INTEGER_DIGITS);
      clean = clampedInt + (decPart ? '.' + decPart : '');

      const num = parseFloat(clean);

      if (!isNaN(num) && Number.isFinite(num)) {
        const formatted = this.formatGermanNumber(num);
        this.internalValue = formatted;

        const emittedValue = Number(num.toFixed(this.getDecimalCount));
        this.$emit('input', emittedValue);
      } else {
        this.internalValue = '';
        this.$emit('input', '');
      }
    },
  },
  computed: {
    thousandSeparator() {
      return this.element?.settings?.thSep || '.';
    },
    decimalSeparator() {
      return this.element?.settings?.decSep || ',';
    },
    isRequired() {
      return this.element.settings?.required || false;
    },
    isDisabled() {
      return this.disabled || this.element.settings?.disabled;
    },
    getPlaceholder() {
      return this.element.settings?.placeholder || '';
    },
    placeholderStyle() {
      return {
        color:
          this.fieldStyle?.placeholderColor ||
          DEFAULT_FORM_STYLES.placeHolderColor,
        fontFamily:
          this.fieldStyle.fontFamily || DEFAULT_FORM_STYLES.placeholderFont,
      };
    },
    getDecimalCount() {
      return this.element?.settings?.toFixed || 2;
    },
    displayValue() {
      if (this.isFocused) return this.internalValue;
      return this.internalValue;
    },
  },
  watch: {
    value(val) {
      if (this.isFocused) return;

      let str = val == null ? '' : String(val);

      if (str.includes('.')) {
        const num = parseFloat(str);
        if (!isNaN(num)) {
          str = this.formatGermanNumber(num);
        }
      }

      if (str !== this.internalValue) {
        this.internalValue = str;
      }
    },
  },
  components: { FormLabel, FormDescription },
};
</script>

<style scoped>
.form-input-element-wrapper:has(input[type='hidden']) .form-label,
.form-input-element-wrapper:has(.form-description) .form-label {
  margin-bottom: 9px;
}

.form-input-element-wrapper
  label:hover
  ~ .input-field-wrapper
  input[type='number']::-webkit-inner-spin-button,
.form-input-element-wrapper
  label:hover
  ~ .input-field-wrapper
  input[type='number']::-webkit-outer-spin-button {
  opacity: 0 !important;
}

.form-input-element-wrapper .form-input {
  height: 65px;
  font-family: 'OpenSans-Regular';
  border: 0.7px solid #dddddd;
  border-radius: 10px;
  padding: 0 23px;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: #222222;
  cursor: auto;
  vertical-align: middle;
}

.form-input-element-wrapper .form-input::placeholder {
  font-weight: 600;
  font-family: v-bind('placeholderStyle.fontFamily');
  color: v-bind('placeholderStyle.color');
}

.form-input-element-wrapper input:focus::placeholder {
  color: transparent !important;
}

.form-input-element-wrapper input:-webkit-autofill {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: v-bind(inputTextColor);
}
.form-input-element-wrapper input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  color: #c5c5c5 !important;
  border-color: #ededed !important;
}
.form-input-element-wrapper input:focus {
  box-shadow: none;
  border-color: transparent !important;
  outline: 2.2px solid #000000;
  outline-offset: -2.2px;
}

.form-input-element-wrapper .input-field-wrapper img.prefix-icon,
.form-input-element-wrapper .input-field-wrapper img.error-icon {
  right: 25px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ff0000;
  width: 21px;
  height: 21px;
  display: none;
  user-drag: none;
  -webkit-user-drag: none;
}
.form-input-element-wrapper .input-field-wrapper img.prefix-icon {
  color: unset;
  display: block;
  left: 20px;
  width: 30px;
  height: 30px;
}
.form-input-element-wrapper:has(.prefix-icon) .form-input {
  padding-left: 60px !important;
}
.was-submitted
  .form-input-element-wrapper:has(.form-input:invalid)
  img.error-icon {
  display: block;
}

.was-submitted .form-input-element-wrapper .form-input:invalid {
  outline: 2.2px solid #ff0000 !important;
  background-color: #ff000008;
  border-color: transparent !important;
  outline-offset: -2.2px;
}
</style>
